import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  ButtonBase,
  DateField,
  IconButton,
  LabelWithIcon,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@pulse/ui';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

import { LINE_CLAMP_2_SX, useIsTruncated } from '../../../components/TruncatableText';
import { formatDdMmYyyy } from '../../../lib/date.util';
import { blockNegativeHourKeys, restrictedHoursChange } from '../../../lib/hours-input.util';

// Shared click-to-edit-in-place primitives — originally built for ProjectPlannerTab.tsx's
// table cells ("click a cell, pick from a menu / type a value"), and reused as-is by
// IssueDetailDialog.tsx's Details tab so every place an issue field can be edited inline behaves
// identically, not just visually similarly.

// A click-to-open popover of discrete choices (Status/Priority/Type/...) — selecting an option
// commits immediately. For a field whose options can run long enough to need searching
// (Module/Sprint/Change Request), use CompactEntityPicker instead (it's generic over any
// `{ id, name }`-shaped option, not just people — pass `hideAvatar` for a non-person `T`).
export function InlineMenuCell({
  trigger,
  options,
  selected,
  onSelect,
  fullWidth = true,
}: {
  trigger: ReactNode;
  options: { value: string; label: string; icon?: ReactNode }[];
  selected: string;
  onSelect: (value: string) => void;
  fullWidth?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: fullWidth ? 'block' : 'inline-flex',
          width: fullWidth ? '100%' : undefined,
          maxWidth: '100%',
          textAlign: 'left',
          borderRadius: 1,
          px: 0.5,
          '&.Mui-focusVisible, &:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
          },
        }}
      >
        {trigger}
      </ButtonBase>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {options.map((opt) => (
          <MenuItem
            key={opt.value || '__empty__'}
            selected={opt.value === selected}
            onClick={() => { onSelect(opt.value); setAnchorEl(null); }}
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 320 }}
          >
            {opt.icon ? <LabelWithIcon icon={opt.icon} label={opt.label} /> : opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

const COMPLETE_DATE = /^\d{4}-\d{2}-\d{2}$/;

// A native date input paired with a dedicated calendar-icon button. The raw input is pulled out
// of the Tab order (`tabIndex: -1`) so keyboard focus lands on the icon button instead of the
// date segments — pressing Enter/Space on it opens the native picker via `showPicker()`. This
// also sidesteps a real Chromium quirk: selecting a date from the native picker's popup can
// dispatch a synthetic Enter keydown on the underlying input, which previously bubbled into
// onKeyDown handlers that treated it as "the user pressed Enter" (e.g. submitting the create
// row) — routing the interaction through a plain button click avoids that entirely.
export function DateEntryField({
  value,
  onChange,
  onCommit,
  onCancel,
  width = 104,
  autoFocus = false,
  bounds,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Fires on blur (except when focus is moving to this field's own calendar button — opening
   *  the picker must not end editing) or Enter. Deciding whether the current value is complete
   *  enough to save (vs. still mid-entry) is the caller's job, same as before — this component
   *  no longer tries to infer that from onChange/validity timing, which wasn't reliable enough
   *  across browsers: a stale or missed validity.badInput read could let a still-incomplete
   *  value look "complete" and trigger a commit mid-keystroke, which looked like the field
   *  losing focus after a single character. Blur/Enter are unambiguous signals instead. */
  onCommit?: () => void;
  /** Escape — revert and stop editing, matching the text/number cells' Cancel behavior. */
  onCancel?: () => void;
  width?: number;
  autoFocus?: boolean;
  /** Native `min`/`max` for the input — greys the unreachable days out in the calendar popup
   *  rather than leaving the user to discover the limit only on save. Advisory only: typing can
   *  still produce an out-of-range value, so a caller that passes bounds must also validate the
   *  committed value (see InlineEditCell's `validateDate`). Absent keys must be *omitted*, not
   *  set to undefined — see dateInputBounds() in utils/issueHierarchyDates.ts for why. */
  bounds?: { min?: string; max?: string };
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Focuses the actual date input, not the calendar button — this field opens from a mouse
    // click on the display value, and a user's very next action is almost always to type a
    // digit straight away. Focusing the button first (its original purpose: letting Enter/Space
    // open the picker for keyboard-only users) meant that very first keystroke landed on a
    // button that ignores digit keys and was silently lost, only registering once the user
    // clicked into the input themselves — read as "the first stroke doesn't work". preventScroll
    // stops the browser from also scrolling the page to re-center this input — this field opens
    // in place of a value the user already tapped/clicked, which is by definition already
    // visible, so no scroll is needed and doing it anyway just reads as the row jumping.
    if (autoFocus) inputRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  // DateField's own onChange (which drives `value` here) only fires once IT has decided the
  // value is genuinely settled — not mid-typing/incomplete, not a padded-year artifact (see
  // DateField.tsx's commit()/isPaddedYearArtifact). Reacting to `value` reaching a complete date
  // reuses that same determination instead of re-deriving "is the user done" from raw DOM events
  // here. That alone isn't quite enough, though: DateField's own artifact guard only covers the
  // *year* segment, so overwriting a single day/month digit of an ALREADY-complete date (e.g.
  // day "5" replacing "12" reads as a valid, complete "05" immediately) still reaches here as a
  // genuine onChange — even though the user is nowhere near done. lastKeydownRef distinguishes
  // that case (a real keystroke landing on an already-complete value) from a calendar-popup pick
  // (no keydown fires on this input) or typing a fresh date through to completion from a
  // previously-incomplete value — only the latter two should auto-close.
  // Also guards against a React 18 StrictMode gotcha: StrictMode deliberately invokes an
  // effect's setup twice (setup → cleanup → setup again) for the same instance/refs. Without
  // this check, an already-complete date opened for editing would pass the keyboard/wasComplete
  // guard below on its first synthetic invocation (viaKeyboard is false pre-typing) and blur
  // itself shut the instant it mounts, before the user can do anything.
  const initialValueRef = useRef(value);
  const lastKeydownRef = useRef(false);
  const wasCompleteRef = useRef(COMPLETE_DATE.test(value));
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const handleKeyDown = () => {
      lastKeydownRef.current = true;
    };
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, []);
  useEffect(() => {
    const viaKeyboard = lastKeydownRef.current;
    const wasComplete = wasCompleteRef.current;
    lastKeydownRef.current = false;
    wasCompleteRef.current = COMPLETE_DATE.test(value);
    if (value === initialValueRef.current) return;
    if (viaKeyboard && wasComplete) return;
    if (COMPLETE_DATE.test(value)) {
      // Blur whichever element is actually focused — normally the input, but selecting a date
      // from the native calendar popup can leave the calendar-icon button focused instead.
      (document.activeElement as HTMLElement | null)?.blur();
    }
  }, [value]);

  function openPicker() {
    const el = inputRef.current;
    if (!el) return;
    if ('showPicker' in el && typeof el.showPicker === 'function') {
      el.showPicker();
    } else {
      el.focus();
    }
  }

  return (
    // minWidth: 0 lets the input genuinely shrink to whatever the surrounding (often narrow,
    // table-layout:fixed) cell actually has available, rather than always demanding its full
    // `width` regardless of the cell — that mismatch was what let the Start Date field's
    // calendar button visually spill into the adjacent Due Date cell.
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <DateField
        inputRef={inputRef}
        size="small"
        variant="standard"
        value={value}
        onChange={onChange}
        onBlur={(e) => {
          if (e.relatedTarget === buttonRef.current) return;
          onCommit?.();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onCommit?.();
          if (e.key === 'Escape') onCancel?.();
        }}
        sx={{
          width,
          minWidth: 0,
          flexShrink: 1,
          '& .MuiInputBase-input': { fontSize: '0.8125rem' },
          '& input::-webkit-calendar-picker-indicator': { display: 'none !important' },
          '& input::-webkit-clear-button': { display: 'none !important' },
          '& .MuiIconButton-root': { padding: '2px' },
          '& .MuiIconButton-root .MuiSvgIcon-root': { fontSize: 16 },
        }}
        slotProps={{ htmlInput: { tabIndex: -1, ...bounds } }}
      />
      <Tooltip title="Open calendar">
        <IconButton
          ref={buttonRef}
          size="small"
          onClick={openPicker}
          aria-label="Open calendar"
          onBlur={(e) => {
            if (e.relatedTarget === inputRef.current) return;
            onCommit?.();
          }}
          sx={{ flexShrink: 0, p: 0.25 }}
        >
          <CalendarTodayIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

// A click-to-edit free-text field (Title/Estimate/Due Date) — clicking the displayed value
// swaps it for an input plus a ✓/✗ confirm-cancel pair, generalized to any free-text cell.
export function InlineEditCell({
  value,
  onSave,
  type = 'text',
  placeholder = '—',
  width = 110,
  validate,
  validateDate,
  onValidationError,
  clearOnInvalidDate = false,
  dateBounds,
  textVariant = 'body2',
  showEditIcon = false,
  noClamp = false,
  stretchTrigger = false,
  maxLines,
}: {
  value: string;
  onSave: (value: string) => void;
  type?: 'text' | 'number' | 'date';
  placeholder?: string;
  width?: number;
  validate?: (draft: string) => boolean;
  /** `type="date"` only — validates the committed value, returning a message to reject it with
   *  or null to accept. Separate from `validate`'s silent boolean because a bounds rejection
   *  needs to say *why*, and an inline date cell in a table row has nowhere to put helper text
   *  of its own: the message goes to `onValidationError` for the page to surface (snackbar),
   *  and the cell reverts to its previous value, same as Cancel. */
  validateDate?: (value: string) => string | null;
  /** Where a `validateDate` rejection message is reported. */
  onValidationError?: (message: string) => void;
  /** `type="date"` only — on a `validateDate` rejection, clear the field (save an empty date)
   *  instead of reverting to the previous value. Off by default (revert), matching every
   *  existing date-cell call site; the Planner's Start/Due Date cells opt in so an out-of-range
   *  edit doesn't silently snap back to the old date with no visible change beyond the snackbar. */
  clearOnInvalidDate?: boolean;
  /** `type="date"` only — native min/max bounds for the picker, passed through to
   *  DateEntryField. Advisory: `validateDate` is what actually blocks a typed value. */
  dateBounds?: { min?: string; max?: string };
  /** Typography variant for the display-mode text — e.g. 'h6' for a title field editing
   *  in place of a heading, instead of the default table-cell-sized 'body2'. */
  textVariant?: 'body2' | 'h6';
  /** Shows a small pencil icon next to the value as an explicit "this is editable" affordance,
   *  in addition to the value itself already being click-to-edit. Off by default — a compact
   *  table cell (Planner) doesn't need it since every cell in that grid is already known to be
   *  editable; a standalone field like an issue's Title benefits from the extra cue. */
  showEditIcon?: boolean;
  /** Skips any clamp/cap so the full value always wraps and displays in full, growing the
   *  surrounding layout as needed — for a breakpoint where the caller itself absorbs that
   *  variable height elsewhere (e.g. the mobile Issue Detail Dialog fixes its own total height
   *  and lets its tab-content area shrink/grow to compensate for the title's own height). Not
   *  for a context that needs a bounded, predictable height on its own — use `maxLines` there
   *  instead. Ignored when `maxLines` is also set. */
  noClamp?: boolean;
  stretchTrigger?: boolean;
  /** Caps the display-mode value at this many lines and makes it scroll (native overflow, no
   *  truncation) once content exceeds that — for a field that can run long (an issue Title) but
   *  shouldn't be allowed to grow the surrounding layout without bound. Takes priority over
   *  `noClamp`/the default 2-line clamp-and-ellipsis when set; the full value is always available
   *  once editing starts either way. */
  maxLines?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // DateField's onChange (which drives updateDraft below) and DateEntryField's onCommit both fire
  // synchronously inside the same native blur event — but setDraft's update isn't visible until
  // the next render, so onCommit's date branch reading the `draft` state variable would still see
  // the value from *before* this blur (e.g. saving the old date right after the user cleared it).
  // draftRef is updated in the same tick as setDraft, so onCommit can read the value that was
  // actually just committed instead of a stale closure.
  const draftRef = useRef(value);
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>();

  // Focused manually (rather than via the TextField's own `autoFocus`) so we can pass
  // `preventScroll: true` — this field opens in place of a value the user already tapped/
  // clicked, which by definition is already visible, so the browser's default "scroll the
  // newly-focused element into view" has nothing useful to do and just reads as the row/card
  // jumping the moment editing starts.
  const editInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (editing) editInputRef.current?.focus({ preventScroll: true });
  }, [editing]);

  // maxLines: whether the value genuinely overflows `maxLines`, measured directly rather than
  // assumed from a `maxLines * lineHeight` guess — font-metric rounding can make exactly-fitting
  // text measure a hair taller than the CSS estimate, which would show a scrollbar for content
  // that isn't actually cut off. A small tolerance (a couple px, not a whole line) absorbs that
  // rounding without ever hiding a genuine extra line.
  const maxLinesRef = useRef<HTMLDivElement>(null);
  const [maxLinesOverflowing, setMaxLinesOverflowing] = useState(false);
  useEffect(() => {
    if (!maxLines) return;
    const el = maxLinesRef.current;
    if (!el) return;
    const check = () => setMaxLinesOverflowing(el.scrollHeight - el.clientHeight > 2);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
    // `editing` is included so leaving edit mode re-attaches the observer to the freshly
    // remounted display Typography — without it, saving with an unchanged `value` (or any
    // edit/cancel round-trip) left the observer watching the old, now-detached node from before
    // editing started, so the scrollbar/overflow state never got recomputed for the new one.
  }, [maxLines, value, editing]);

  const editUnderlineMirrorRef = useRef<HTMLDivElement>(null);
  const editFieldWrapperRef = useRef<HTMLDivElement>(null);
  const [editUnderlineTop, setEditUnderlineTop] = useState<number | undefined>(undefined);

  const [isEditFieldFocused, setIsEditFieldFocused] = useState(false);
  useLayoutEffect(() => {
    if (!maxLines || !editing) return;
    const mirror = editUnderlineMirrorRef.current;
    const inputRoot = editFieldWrapperRef.current?.querySelector<HTMLElement>('.MuiInputBase-root');
    if (!mirror || !inputRoot) return;
    setEditUnderlineTop(Math.min(mirror.scrollHeight, inputRoot.clientHeight));
  }, [maxLines, editing, draft]);

  function updateDraft(v: string) {
    draftRef.current = v;
    setDraft(v);
  }

  // For a noClamp field (no display-mode cap — e.g. the issue Title on mobile), the editable
  // TextField has no natural height ceiling of its own: left alone, it grows with every line
  // typed and takes the surrounding dialog with it. Rather than impose an arbitrary row limit,
  // capture the height display mode was *actually* using the moment editing starts (measured,
  // not estimated) and cap the textarea there, scrolling internally past it — editing can't
  // make the dialog grow (or shrink) at all. Only meaningful for noClamp without maxLines —
  // maxLines already gets an equivalent, simpler cap via TextField's maxRows.
  const editMaxHeightRef = useRef<number | undefined>(undefined);

  function startEdit() {
    if (noClamp && !maxLines) {
      editMaxHeightRef.current = ref.current?.getBoundingClientRect().height;
    }
    draftRef.current = value;
    setDraft(value);
    setEditing(true);
  }

  function confirm() {
    if (validate && !validate(draft)) return;
    onSave(draft);
    setEditing(false);
  }

  function cancel() {
    draftRef.current = value;
    setDraft(value);
    setEditing(false);
  }

  if (!editing) {
    const trigger = (
      <ButtonBase
        onClick={startEdit}
        sx={{
          display: 'block',
          width: !showEditIcon || stretchTrigger ? '100%' : undefined,
          flex: showEditIcon && stretchTrigger ? 1 : undefined,
          minWidth: 0,
          textAlign: 'left',
          borderRadius: 1,
          px: 0.5,
          '&.Mui-focusVisible, &:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
          },
        }}
      >
        {type === 'text' ? (
          maxLines ? (
            <Typography
              ref={maxLinesRef}
              component="div"
              variant={textVariant}
              color={value ? 'text.primary' : 'text.secondary'}
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.5em',
                // A fixed height (not just a cap) so a short value still reserves the full
                // maxLines-worth of space — keeps the dialog's overall height constant across
                // issues instead of shrinking to fit a short title and growing for a long one.
                height: `${maxLines * 1.5}em`,
                overflowY: maxLinesOverflowing ? 'auto' : 'hidden',
                py: 0.25,
                // Reserves the top-right corner for the overlaid edit icon (see below) so wrapped
                // text doesn't run underneath it — only the first couple of lines are affected in
                // practice, but the padding applies to every line for a simple, even wrap.
                pr: showEditIcon ? 4.5 : undefined,
              }}
            >
              {value || placeholder}
            </Typography>
          ) : (
            <Tooltip title={!noClamp && isTruncated && value ? value : ''}>
              <Typography
                ref={ref}
                variant={textVariant}
                color={value ? 'text.primary' : 'text.secondary'}
                sx={noClamp ? { wordBreak: 'break-word', py: 0.25 } : { ...LINE_CLAMP_2_SX, py: 0.25 }}
              >
                {value || placeholder}
              </Typography>
            </Tooltip>
          )
        ) : (
          <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'} sx={{ whiteSpace: 'nowrap', py: 0.25 }}>
            {value ? (type === 'date' ? formatDdMmYyyy(value) : value) : placeholder}
          </Typography>
        )}
      </ButtonBase>
    );
    if (!showEditIcon) return trigger;
    if (maxLines) {
      // Overlaid within the scroll viewport's own bounds (not a separate column beside it) —
      // same reasoning as the Description field's edit icon in IssueDetailDialog.tsx: it reads
      // as part of the same bounded box regardless of scroll position, instead of floating
      // beside a box that can scroll independently of it.
      return (
        <Box sx={{ position: 'relative' }}>
          {trigger}
          <IconButton
            size="small"
            onClick={startEdit}
            aria-label="Edit"
            sx={{
              position: 'absolute',
              top: 2,
              // Offset past the native scrollbar this box renders when its content overflows
              // (overflowY: auto with no custom scrollbar styling) — right: 2 alone sat the icon
              // right on top of the scrollbar track/thumb instead of clear of it.
              right: 18,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }
    return (
      <Stack direction="row" alignItems="flex-start" spacing={0.25}>
        {trigger}
        <IconButton size="small" onClick={startEdit} aria-label="Edit" sx={{ flexShrink: 0 }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  }

  if (type === 'date') {
    return (
      <DateEntryField
        value={draft}
        onChange={updateDraft}
        onCommit={() => {
          const committed = draftRef.current;
          if (committed === '' || /^\d{4}-\d{2}-\d{2}$/.test(committed)) {
            const rejection = validateDate?.(committed) ?? null;
            if (rejection) {
              onValidationError?.(rejection);
              if (clearOnInvalidDate) {
                // Clear rather than revert — an out-of-range edit shouldn't silently snap back
                // to the old date with no visible change beyond the snackbar.
                onSave('');
                setEditing(false);
              } else {
                // Revert rather than save, and let the caller explain why — the value never
                // reaches the API, so there's no request to fail and report on instead.
                cancel();
              }
              return;
            }
            onSave(committed);
          }
          // Still incomplete on blur/Enter — just close without saving, same as Cancel.
          setEditing(false);
        }}
        onCancel={cancel}
        width={width}
        autoFocus
        bounds={dateBounds}
      />
    );
  }

  const editIsMultiline = type === 'text' && (Boolean(maxLines) || noClamp);
  // maxLines already gets a row-count cap via TextField's own maxRows above; a noClamp-only
  // field (no maxLines) instead gets the measured-pixel cap captured in startEdit().
  const editMaxHeight = noClamp && !maxLines ? editMaxHeightRef.current : undefined;
  const editNeedsStableUnderline = Boolean(editMaxHeight) || Boolean(maxLines);
  const editInputFontSx = textVariant === 'h6' ? { fontSize: '1.25rem', fontWeight: 500 } : { fontSize: '0.8125rem' };
  // Confirm/Cancel normally sit flush with the field's own bottom (flex-end) — right where a
  // plain border-bottom underline would be. A maxLines field's underline no longer sits at that
  // fixed bottom edge (it tracks the actual text, per editUnderlineTop above), so pin these to
  // the top of the row instead and nudge them down to the same line via a transform (a plain
  // margin-top here doesn't reliably take effect as a flex-item cross-axis offset within this
  // Stack — transform does).
  const editIconButtonsOffset =
    maxLines && editUnderlineTop !== undefined ? Math.max(editUnderlineTop - 16, 0) : undefined;
  return (
    <Stack
      direction="row"
      spacing={0.25}
      alignItems={editIsMultiline ? (maxLines ? 'flex-start' : 'flex-end') : 'center'}
    >
      <Box ref={editFieldWrapperRef} sx={{ position: 'relative', flex: type === 'text' ? 1 : undefined, minWidth: 0 }}>
        <TextField
          size="small"
          variant="standard"
          type={type}
          inputRef={editInputRef}
          fullWidth={type === 'text'}
          multiline={editIsMultiline}
          // minRows === maxRows forces a constant row count regardless of actual content length —
          // matching the display Typography's fixed `height` above, so entering/leaving edit mode
          // never changes the dialog's height.
          minRows={type === 'text' ? maxLines : undefined}
          maxRows={type === 'text' ? maxLines : undefined}
          value={draft}
          onChange={type === 'number' ? restrictedHoursChange(setDraft) : (e) => setDraft(e.target.value)}
          onKeyDown={(e: ReactKeyboardEvent<HTMLInputElement>) => {
            if (type === 'number') blockNegativeHourKeys(e);
            if (editIsMultiline) {
              if (e.key === 'Escape') cancel();
              return;
            }
            if (e.key === 'Enter') confirm();
            if (e.key === 'Escape') cancel();
          }}
          // A freshly-focused text field with a pre-filled value places the caret at the end, and
          // the browser auto-scrolls the nearest scrollable ancestor (our height-capped
          // .MuiInputBase-root) to keep that caret in view — hiding the start of the text. Moving
          // the caret to position 0 makes the browser's own "keep caret visible" behavior show the
          // top instead, matching normal top-to-bottom reading order.
          onFocus={
            editNeedsStableUnderline
              ? (e: ReactFocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  if (maxLines) setIsEditFieldFocused(true);
                  const el = e.target;
                  el.setSelectionRange(0, 0);
                  const root = el.closest('.MuiInputBase-root');
                  if (root instanceof HTMLElement) root.scrollTop = 0;
                }
              : undefined
          }
          onBlur={maxLines ? () => setIsEditFieldFocused(false) : undefined}
          slotProps={type === 'number' ? { htmlInput: { min: 0, step: 'any' } } : undefined}
          sx={
            type === 'text'
              ? {
                  '& .MuiInputBase-input': editInputFontSx,
                  ...(editNeedsStableUnderline
                    ? {
                        '& .MuiInputBase-root': {
                          ...(editMaxHeight ? { maxHeight: editMaxHeight, overflowY: 'auto', pr: 0.5 } : {}),
                          alignItems: 'flex-start',
                          '&:before, &:after': { display: 'none' },
                          // maxLines gets its own dynamically-positioned underline below (the
                          // fixed-height box can't use a plain border-bottom — see
                          // editUnderlineTop's comment); editMaxHeight's box already shrinks to
                          // fit content, so a plain border-bottom is already correctly placed.
                          ...(maxLines
                            ? {}
                            : { borderBottom: '1px solid', borderColor: 'rgba(0, 0, 0, 0.42)' }),
                        },
                        '& .MuiInputBase-root.Mui-focused': maxLines
                          ? {}
                          : { borderBottom: '2px solid', borderColor: 'primary.main' },
                      }
                    : {}),
                }
              : {
                  width,
                  '& .MuiInputBase-input': { fontSize: '0.8125rem' },
                  '& input[type=number]': { MozAppearance: 'textfield' },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }
          }
        />
        {maxLines && (
          <>
            <Box
              ref={editUnderlineMirrorRef}
              aria-hidden
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.4375,
                ...editInputFontSx,
              }}
            >
              {draft || ' '}
            </Box>
            <Box
              sx={
                isEditFieldFocused
                  ? { position: 'absolute', left: 0, right: 0, top: editUnderlineTop, borderBottom: '2px solid', borderColor: 'primary.main' }
                  : { position: 'absolute', left: 0, right: 0, top: editUnderlineTop, borderBottom: '1px solid', borderColor: 'rgba(0, 0, 0, 0.42)' }
              }
            />
          </>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={0}
        sx={{
          transform: editIconButtonsOffset !== undefined ? `translateY(${editIconButtonsOffset}px)` : undefined,
        }}
      >
        <IconButton size="small" color="success" onClick={confirm} aria-label="Confirm" sx={{ p: 0.25 }}>
          <CheckIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton size="small" onClick={cancel} aria-label="Cancel" sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>
    </Stack>
  );
}
